// (c) 2023 ABBYY Development, Inc.

import { FC, useRef, useState } from 'react';
import {
    Typography,
    IconLabel,
    Dot,
    AlertsWarningFill,
    Popper
} from 'components';
import { useTranslation } from 'react-i18next';
import * as Constants from '../domain/recorders/constants';
import styled from 'styled-components';
import { AGENT_STATUS } from 'domain/agents/contants';

export interface StatusProps {
    status: string;
    outdated?: boolean;
}

const PopperWrapper = styled.div`
    padding-left: 0.5rem;
`;

const OutdatedWarning = () => {
    const { t } = useTranslation();
    const iconRef = useRef<HTMLDivElement>();
    const [showPopper, setShowPopper] = useState(false);

    return (
        <PopperWrapper
            ref={iconRef}
            onMouseEnter={() => setShowPopper(true)}
            onMouseLeave={() => setShowPopper(false)}
        >
            <Popper
                isOpen={showPopper}
                placement="left"
                anchorElement={iconRef.current}
                hasArrow
            >
                {t('expressions.outdated_recorder')}
            </Popper>
            <AlertsWarningFill size={16} color="warning" />
        </PopperWrapper>
    );
};

export const Status: FC<StatusProps> = ({ status, outdated }) => {
    const { t } = useTranslation();

    switch (status) {
        case Constants.ONLINE_STATUS:
            return (
                <IconLabel>
                    <Dot color="success" size={20} />
                    <Typography variant="body2" weight="regular">
                        {t('status.online')}
                    </Typography>
                    {outdated && <OutdatedWarning />}
                </IconLabel>
            );
        case Constants.RECORDING_STATUS:
            return (
                <IconLabel>
                    <Dot color="error" size={20} />
                    <Typography variant="body2" weight="regular">
                        {t('status.recording')}
                    </Typography>
                    {outdated && <OutdatedWarning />}
                </IconLabel>
            );
        case Constants.OFFLINE_STATUS:
            return (
                <IconLabel>
                    <Dot color={['grey', 'A200']} size={20} />
                    <Typography variant="body2" weight="regular">
                        {t('status.offline')}
                    </Typography>
                </IconLabel>
            );
        case AGENT_STATUS.UNHEALTHY:
            return (
                <IconLabel>
                    <Dot color="warning" size={20} />
                    <Typography variant="body2" weight="regular">
                        {t('status.unhealthy')}
                    </Typography>
                </IconLabel>
            );
        default:
            return null;
    }
};
